import { Edit2, Mail, Plus, User, UserCog } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { apiUrl } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import { authDelete, authGet, authPost, authPut } from '@/lib/authFetch';
import { type SystemAdminFormData, SystemAdminModal, type SystemAdminRole } from './SystemAdminModal';

const API_BASE = apiUrl('/api/system/admins');

type SystemAdminStatus = 'active' | 'suspended' | 'deleted';

interface SystemAdmin {
  id: string;
  email: string;
  name: string;
  role: SystemAdminRole;
  status: SystemAdminStatus;
  createdAt: string;
  vendorId?: string;
  vendor?: {
    name: string;
  };
}

interface Vendor {
  id: string;
  name: string;
  name_en?: string;
}

const ROLE_COLORS: Record<SystemAdminRole, string> = {
  system_admin: 'bg-red-100 text-red-800',
  system_operator: 'bg-blue-100 text-blue-800',
  system_support: 'bg-green-100 text-green-800',
};

// Sort helper
const SortIndicator = ({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) => {
  if (!active) return null;
  return <span className="ml-1">{direction === 'asc' ? '▲' : '▼'}</span>;
};

// Pagination sizes
const PAGE_SIZES = [10, 20, 50, 100];

export default function SystemAdminList() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [admins, setAdmins] = useState<SystemAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<SystemAdmin | null>(null);

  // Filters
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [vendorFilter, setVendorFilter] = useState<string>('');
  const [showSuspended, setShowSuspended] = useState(false);

  // Sorting
  const [sortKey, setSortKey] = useState<'name' | 'email' | 'createdAt'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Delete Confirmation State (Danger Zone)
  const [deleteConfirmationName, setDeleteConfirmationName] = useState('');

  const [vendors, setVendors] = useState<Vendor[]>([]);

  useEffect(() => {
    const loadVendors = async () => {
      try {
        const res = await authGet(apiUrl('/api/vendors'));
        if (res.ok) {
          setVendors(await res.json());
        }
      } catch (error_) {
        console.error(error_);
      }
    };
    loadVendors();
  }, []);

  const fetchAdmins = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await authGet(API_BASE);
      if (response.ok) {
        const data = (await response.json()) as SystemAdmin[];
        setAdmins(data);
      } else {
        setError(t('common.errors.fetchFailed'));
      }
    } catch (error_) {
      console.error(error_);
      setError(t('common.errors.networkError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  // Derived State: Filtering & Sorting
  const filteredAdmins = admins
    .filter((a) => {
      if (!showSuspended && a.status === 'suspended') return false;
      if (roleFilter && a.role !== roleFilter) return false;
      if (vendorFilter && a.vendorId !== vendorFilter) return false;
      return true;
    })
    .sort((a, b) => {
      const valA = (a[sortKey] || '').toLowerCase();
      const valB = (b[sortKey] || '').toLowerCase();
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  // Pagination Logic
  const totalItems = filteredAdmins.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedAdmins = filteredAdmins.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Handlers
  const handleSort = (key: 'name' | 'email' | 'createdAt') => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleSubmit = async (data: SystemAdminFormData) => {
    try {
      if (editingAdmin) {
        await authPut(`${API_BASE}/${editingAdmin.id}`, data);
      } else {
        await authPost(API_BASE, data);
      }
      setIsModalOpen(false);
      setEditingAdmin(null);
      fetchAdmins();
    } catch (error_) {
      console.error(error_);
      setError(t('common.errors.saveFailed'));
    }
  };

  const handleToggleStatus = async (admin: SystemAdmin) => {
    if (user?.id === admin.id) {
      alert(t('common.errors.cannotSuspendSelf', { defaultValue: 'Cannot suspend your own account' }));
      return;
    }
    const newStatus = admin.status === 'active' ? 'suspended' : 'active';
    const msg =
      newStatus === 'active'
        ? t('common.confirm.activate', { name: admin.name })
        : t('common.confirm.deactivate', { name: admin.name });

    if (!confirm(msg)) return;

    try {
      await authPut(`${API_BASE}/${admin.id}`, { status: newStatus });
      fetchAdmins();
    } catch (error_) {
      console.error(error_);
      setError(t('common.errors.saveFailed'));
    }
  };

  const handleHardDelete = async () => {
    if (!editingAdmin) return;
    if (deleteConfirmationName !== editingAdmin.name) return;

    if (!confirm(t('common.confirm.delete'))) return;

    try {
      await authDelete(`${API_BASE}/${editingAdmin.id}`);
      setIsModalOpen(false);
      fetchAdmins();
    } catch (error_) {
      console.error(error_);
      setError(t('common.errors.deleteFailed'));
    }
  };

  const openEditModal = (admin: SystemAdmin) => {
    setEditingAdmin(admin);
    setDeleteConfirmationName('');
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingAdmin(null);
    setIsModalOpen(true);
  };

  const renderTableContent = () => {
    if (isLoading) {
      return <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>;
    }
    if (error) {
      return <div className="p-8 text-center text-red-500">{error}</div>;
    }
    if (filteredAdmins.length === 0) {
      return <div className="p-8 text-center text-gray-500">{t('staff.messages.noData')}</div>;
    }

    return (
      <>
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-gray-500 uppercase font-medium border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name')}>
                <div className="flex items-center gap-1">
                  {t('staff.fields.name')}
                  <SortIndicator active={sortKey === 'name'} direction={sortDir} />
                </div>
              </th>
              <th className="px-6 py-4">{t('staff.fields.vendor')}</th>
              <th className="px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('email')}>
                <div className="flex items-center gap-1">
                  {t('staff.fields.email')}
                  <SortIndicator active={sortKey === 'email'} direction={sortDir} />
                </div>
              </th>
              <th className="px-6 py-4">{t('staff.fields.role')}</th>
              <th className="px-6 py-4">{t('staff.fields.status')}</th>
              <th className="px-6 py-4 text-right">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {paginatedAdmins.map((admin) => {
              const statusLabel = t(`staff.status.${admin.status}`);
              const statusColor =
                admin.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600';

              return (
                <tr key={admin.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      {admin.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{admin.vendor?.name || '---'}</td>
                  <td className="px-6 py-4 text-gray-600">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      {admin.email}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[admin.role]}`}>
                      {t(`staff.roles.${admin.role}.label`)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>{statusLabel}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {admin.status !== 'deleted' && (
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(admin)}
                          disabled={user?.id === admin.id}
                          className={`px-2 py-1 rounded text-xs border ${
                            admin.status === 'active'
                              ? 'border-orange-200 text-orange-600 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed'
                              : 'border-green-200 text-green-600 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed'
                          }`}
                        >
                          {admin.status === 'active' ? t('staff.deactivate') : t('staff.activate')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openEditModal(admin)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors text-xs"
                        title={t('staff.edit')}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Pagination Control */}
        <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">
            {t('common.pagination.total', { count: totalItems, defaultValue: `Total ${totalItems} items` })}
          </div>
          <div className="flex items-center gap-4">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {t('common.pagination.perPage', { count: size, defaultValue: `${size}件 / ページ` })}
                </option>
              ))}
            </select>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                {t('common.previous')}
              </Button>
              <span className="px-3 py-1 flex items-center text-sm">
                {currentPage} / {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-gray-900">
            <UserCog className="w-7 h-7 text-gray-700" />
            {t('staff.title')}
          </h1>
          <p className="text-gray-500 mt-1">{t('staff.description')}</p>
        </div>
        <Button onClick={openCreateModal} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          {t('staff.create')}
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-sm flex flex-wrap gap-4 items-center">
        <select
          value={vendorFilter}
          onChange={(e) => {
            setVendorFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        >
          <option value="">{t('staff.filters.allVendors')}</option>
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>

        <select
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        >
          <option value="">{t('staff.filters.allRoles')}</option>
          {(Object.keys(ROLE_COLORS) as SystemAdminRole[]).map((role) => (
            <option key={role} value={role}>
              {t(`staff.roles.${role}.label`)}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={showSuspended} onChange={(e) => setShowSuspended(e.target.checked)} />
          {t('staff.filters.showSuspended')}
        </label>
      </div>

      {/* Admin Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">{renderTableContent()}</div>

      <SystemAdminModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        initialData={editingAdmin}
        vendors={vendors}
        onDelete={handleHardDelete}
        deleteConfirmationName={deleteConfirmationName}
        onDeleteConfirmationNameChange={setDeleteConfirmationName}
      />
    </div>
  );
}
